import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, Clock, Percent, Building2, Phone, ExternalLink,
  Shield, CheckCircle2, User, ChevronRight, Ticket, Loader2,
  Lock, MessageCircle, Sparkles, Info
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { membersApi } from "@/lib/api/directory";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DEAL_IMAGES = [
  "https://images.pexels.com/photos/3845810/pexels-photo-3845810.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop",
  "https://images.pexels.com/photos/4226219/pexels-photo-4226219.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop",
  "https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop",
  "https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop",
];

const RELATED_IMAGES = [
  "https://images.pexels.com/photos/4225880/pexels-photo-4225880.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/3938022/pexels-photo-3938022.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/4226256/pexels-photo-4226256.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/4226269/pexels-photo-4226269.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
];

const TYPE_LABELS = {
  CLINIC: { en: "Clinic", ar: "عيادة" },
  HOSPITAL: { en: "Hospital", ar: "مستشفى" },
  PHARMACY: { en: "Pharmacy", ar: "صيدلية" },
  LAB: { en: "Laboratory", ar: "معمل" },
  RADIOLOGY_CENTER: { en: "Radiology Center", ar: "مركز أشعة" },
  OTHER: { en: "Other", ar: "أخرى" },
};

export default function DealDetailPage() {
  const { id } = useParams();
  const { lang, isRTL } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimed, setClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [memberCode, setMemberCode] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/public/deals/${id}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!user || user.role !== "MEMBER") return;
    let active = true;
    Promise.all([membersApi.listClaims(), membersApi.me()])
      .then(([claimsRes, me]) => {
        if (!active) return;
        const has = (claimsRes?.items || []).some(
          (c) => c.offer_id === id && (c.status || "CLAIMED") !== "CANCELLED"
        );
        setClaimed(has);
        setMemberCode(me?.member_code || null);
      })
      .catch(() => { /* not blocking */ });
    return () => { active = false; };
  }, [id, user]);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await membersApi.claimOffer(id);
      setClaimed(true);
      toast.success(lang === "ar"
        ? "تم حجز العرض — رقم العيادة ظاهر الآن"
        : "Deal claimed — the clinic's number is now visible");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!data?.deal) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">{lang === "ar" ? "العرض غير متاح" : "Deal not found or expired"}</p>
        <Button variant="outline" onClick={() => navigate("/search")}>{lang === "ar" ? "تصفح العروض" : "Browse Deals"}</Button>
      </div>
    );
  }

  const { deal, provider, staff, related_deals } = data;
  const isPercentage = deal.discount_type === "PERCENTAGE";
  const discountedPrice = deal.original_price && deal.discount_type === "FIXED_PACKAGE"
    ? deal.discount_value
    : (deal.original_price ? Math.round(deal.original_price * (1 - deal.discount_value / 100)) : null);
  const estimatedSavings = deal.original_price && isPercentage
    ? Math.round(deal.original_price * deal.discount_value / 100)
    : (deal.original_price && discountedPrice != null ? deal.original_price - discountedPrice : null);
  const isMember = user && user.role === "MEMBER";
  const imgSrc = deal.image_url || DEAL_IMAGES[id.charCodeAt(0) % DEAL_IMAGES.length];
  const phone = provider?.primary_phone || provider?.phone_1; // legacy fallback
  const whatsappRaw = provider?.primary_whatsapp || phone;
  const whatsappLink = whatsappRaw
    ? `https://wa.me/${String(whatsappRaw).replace(/[^0-9]/g, "")}`
    : null;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <PublicHeader />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <div className="relative rounded-2xl overflow-hidden h-56 sm:h-72">
              <img src={imgSrc} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute top-4 end-4 bg-primary text-white text-lg font-bold px-4 py-2 rounded-xl flex items-center gap-1.5 shadow-lg">
                <Percent className="h-5 w-5" />
                {deal.discount_value}% {lang === "ar" ? "خصم" : "OFF"}
              </div>
            </div>

            {/* Deal Info */}
            <div className="bg-white rounded-2xl border border-border/50 p-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-4" data-testid="deal-title">
                {lang === "ar" ? deal.title_ar : deal.title_en}
              </h1>

              {deal.original_price && deal.discount_type === "FIXED_PACKAGE" && (
                <div className="flex flex-wrap items-baseline gap-3 mb-5">
                  <span className="text-3xl font-bold text-primary">
                    {discountedPrice} {lang === "ar" ? "ج.م" : "EGP"}
                  </span>
                  <span className="text-lg text-muted-foreground line-through">
                    {deal.original_price} {lang === "ar" ? "ج.م" : "EGP"}
                  </span>
                  <span className="text-sm font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
                    {lang === "ar" ? "توفير" : "Save"} {deal.original_price - discountedPrice} {lang === "ar" ? "ج.م" : "EGP"}
                  </span>
                </div>
              )}
              {isPercentage && estimatedSavings != null && (
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="text-sm font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full inline-flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    {lang === "ar" ? `وفر ~${estimatedSavings} ج.م` : `Save ~${estimatedSavings} EGP`}
                  </span>
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    {lang === "ar"
                      ? `بناءً على متوسط فاتورة ${deal.original_price} ج.م`
                      : `Based on a typical bill of ${deal.original_price} EGP`}
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-5 pb-5 border-b border-border/40">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>{lang === "ar" ? "صالح حتى" : "Valid until"} {deal.end_date}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-primary" />
                  <span>{(provider?.neighborhoods || []).join(", ")}</span>
                </div>
              </div>

              {/* Description */}
              {(deal.description_en || deal.description_ar) && deal.description_en !== "DEMO_OFFER" && (
                <div className="mb-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2">{lang === "ar" ? "تفاصيل العرض" : "Deal Details"}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {lang === "ar" ? deal.description_ar : deal.description_en}
                  </p>
                </div>
              )}

              {/* Terms */}
              {(deal.terms_en || deal.terms_ar) && (
                <div className="bg-background rounded-xl p-4 border border-border/40">
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    {lang === "ar" ? "الشروط والأحكام" : "Terms & Conditions"}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {lang === "ar" ? deal.terms_ar : deal.terms_en}
                  </p>
                </div>
              )}
            </div>

            {/* Provider Profile Card */}
            {provider && (
              <div className="bg-white rounded-2xl border border-border/50 p-6" data-testid="provider-profile">
                <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-4">
                  {lang === "ar" ? "مقدم الخدمة" : "Provider"}
                </h3>
                <div className="flex items-start gap-4">
                  <div className="h-16 w-16 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                    {provider.logo_url ? (
                      <img src={provider.logo_url} alt="" className="h-10 w-10 object-contain rounded-lg" />
                    ) : (
                      <Building2 className="h-8 w-8 text-primary/40" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-base font-semibold text-foreground">{provider.corporate_name}</h4>
                      <span className="text-[11px] font-medium text-primary bg-secondary px-2 py-0.5 rounded-full">
                        {lang === "ar" ? TYPE_LABELS[provider.type]?.ar : TYPE_LABELS[provider.type]?.en}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {lang === "ar" ? provider.description_ar : provider.description_en}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>{(provider.neighborhoods || []).join(", ")}</span>
                    </div>
                    <Link
                      to={`/provider/${provider.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                      data-testid="view-provider-link"
                    >
                      {lang === "ar" ? "عرض ملف مقدم الخدمة" : "View Provider Profile"}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Staff / Doctors (if linked) */}
            {staff && staff.length > 0 && (
              <div className="bg-white rounded-2xl border border-border/50 p-6" data-testid="deal-staff">
                <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-4">
                  {lang === "ar" ? "الأطباء" : "Doctors"}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {staff.map(s => (
                    <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/40 hover:border-primary/30 transition-colors">
                      <div className="h-11 w-11 rounded-full bg-secondary flex items-center justify-center shrink-0">
                        {s.photo_url ? (
                          <img src={s.photo_url} alt="" className="h-11 w-11 rounded-full object-cover" />
                        ) : (
                          <User className="h-5 w-5 text-primary/40" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {lang === "ar" ? `${s.title_ar || ""} ${s.full_name_ar || ""}` : `${s.title_en || ""} ${s.full_name_en || ""}`}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {lang === "ar" ? s.role_ar : s.role_en}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Deals */}
            {related_deals && related_deals.length > 0 && (
              <div data-testid="related-deals">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  {lang === "ar" ? "عروض مشابهة" : "Related Deals"}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {related_deals.map((rd, i) => {
                    const rdPrice = rd.original_price ? Math.round(rd.original_price * (1 - rd.discount_value / 100)) : null;
                    const rdImg = rd.image_url || RELATED_IMAGES[i % RELATED_IMAGES.length];
                    return (
                      <Link
                        key={rd.id}
                        to={`/deal/${rd.id}`}
                        className="rounded-xl border border-border/50 bg-white overflow-hidden hover:shadow-md transition-shadow flex"
                        data-testid={`related-deal-${rd.id}`}
                      >
                        <div className="w-28 h-28 shrink-0 overflow-hidden">
                          <img src={rdImg} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="p-3 flex-1 min-w-0 flex flex-col justify-center">
                          <p className="text-xs text-muted-foreground truncate mb-1">{rd.provider_name}</p>
                          <p className="text-sm font-semibold text-foreground line-clamp-2 leading-snug mb-1">
                            {lang === "ar" ? rd.title_ar : rd.title_en}
                          </p>
                          <div className="flex items-baseline gap-2">
                            {rdPrice && <span className="text-sm font-bold text-primary">{rdPrice} {lang === "ar" ? "ج.م" : "EGP"}</span>}
                            <span className="text-xs font-medium text-white bg-primary px-1.5 py-0.5 rounded">
                              {rd.discount_value}%
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar CTA */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* Price CTA Card */}
              <div className="bg-white rounded-2xl border border-border/50 p-6 shadow-sm" data-testid="deal-cta-card">
                {deal.original_price && deal.discount_type === "FIXED_PACKAGE" && (
                  <div className="text-center mb-5 pb-5 border-b border-border/40">
                    <span className="text-4xl font-bold text-primary">{discountedPrice}</span>
                    <span className="text-sm text-muted-foreground ms-1">{lang === "ar" ? "ج.م" : "EGP"}</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {lang === "ar" ? "بدلاً من" : "Instead of"}{" "}
                      <span className="line-through">{deal.original_price}</span> {lang === "ar" ? "ج.م" : "EGP"}
                    </p>
                  </div>
                )}
                {isPercentage && estimatedSavings != null && (
                  <div className="text-center mb-5 pb-5 border-b border-border/40">
                    <span className="text-4xl font-bold text-primary">{deal.discount_value}%</span>
                    <span className="text-sm text-muted-foreground ms-1">{lang === "ar" ? "خصم" : "OFF"}</span>
                    <p className="text-xs text-green-700 mt-1.5 font-medium">
                      {lang === "ar" ? `وفر ~${estimatedSavings} ج.م في المتوسط` : `Save ~${estimatedSavings} EGP on average`}
                    </p>
                  </div>
                )}

                {isMember ? (
                  <>
                    {claimed ? (
                      <>
                        <div
                          className="w-full h-12 bg-green-50 text-green-700 border border-green-200 font-semibold text-base rounded-xl mb-3 inline-flex items-center justify-center gap-2"
                          data-testid="claim-deal-claimed"
                        >
                          <CheckCircle2 className="h-5 w-5" />
                          {lang === "ar" ? "تم الحجز" : "Deal Claimed"}
                        </div>

                        {phone ? (
                          <a
                            href={`tel:${phone}`}
                            className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold text-base rounded-xl mb-2 inline-flex items-center justify-center gap-2"
                            data-testid="book-deal-btn"
                          >
                            <Phone className="h-5 w-5" />
                            {phone}
                          </a>
                        ) : null}

                        {whatsappLink ? (
                          <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full h-11 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#128C7E] font-semibold text-sm rounded-xl mb-3 inline-flex items-center justify-center gap-2 border border-[#25D366]/30"
                            data-testid="whatsapp-deal-btn"
                          >
                            <MessageCircle className="h-4 w-4" />
                            {lang === "ar" ? "تواصل عبر واتساب" : "Message on WhatsApp"}
                          </a>
                        ) : null}

                        <div className="rounded-xl bg-secondary/50 border border-border/50 px-3 py-2.5 mt-2">
                          <p className="text-xs text-foreground/80 leading-relaxed">
                            {lang === "ar"
                              ? "اتصل بالعيادة لحجز موعدك واذكر "
                              : "Call the clinic to book your appointment and mention "}
                            <span className="font-semibold">
                              {lang === "ar" ? "كود حماية: " : "your Hemaia code: "}
                              {memberCode || "—"}
                            </span>
                            {lang === "ar" ? " لتطبيق الخصم." : " to apply the discount."}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={handleClaim}
                          disabled={claiming}
                          className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold text-base rounded-xl mb-3"
                          data-testid="claim-deal-btn"
                        >
                          {claiming
                            ? <Loader2 className="h-5 w-5 me-2 animate-spin" />
                            : <Ticket className="h-5 w-5 me-2" />}
                          {lang === "ar" ? "احجز هذا العرض" : "Claim this Deal"}
                        </Button>

                        <div className="rounded-xl bg-secondary/40 border border-dashed border-border/60 px-3 py-3 inline-flex items-center justify-center gap-2 w-full">
                          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {lang === "ar"
                              ? "احجز العرض لإظهار رقم العيادة"
                              : "Claim to reveal the clinic's contact number"}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold text-base rounded-xl mb-4"
                      onClick={() => navigate("/signup")}
                      data-testid="subscribe-deal-btn"
                    >
                      {lang === "ar" ? "اشترك لحجز هذا العرض" : "Subscribe to Book this Deal"}
                    </Button>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-foreground">{lang === "ar" ? "اشتراك سنوي منخفض" : "Low Annual Fee"}</p>
                          <p className="text-[11px] text-muted-foreground">{lang === "ar" ? "اشترك مرة واحدة ووفر طوال السنة" : "One subscription, savings all year"}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-foreground">{lang === "ar" ? "خصومات كبيرة" : "Big Savings"}</p>
                          <p className="text-[11px] text-muted-foreground">{lang === "ar" ? "خصومات تصل إلى 40% في كل زيارة" : "Up to 40% off on every visit"}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-foreground">{lang === "ar" ? "شبكة واسعة" : "Large Network"}</p>
                          <p className="text-[11px] text-muted-foreground">{lang === "ar" ? "مئات العيادات والمستشفيات" : "Hundreds of clinics and hospitals"}</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Trust Card */}
              <div className="bg-white rounded-2xl border border-border/50 p-5 space-y-3">
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <Shield className="h-4 w-4 text-primary shrink-0" />
                  <span>{lang === "ar" ? "مقدمو خدمة معتمدون من وزارة الصحة" : "Ministry of Health verified providers"}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  <span>{lang === "ar" ? "خصم فوري عند الزيارة" : "Instant discount on visit"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
